// src/pages/Lounge/ExploreStreetSingle/ExploreStreetSingle.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion as Motion } from "framer-motion";
import Loading from "@/components/Loading/Loading";

import CityImage from "@/assets/UI/city.webp";
import api from "@/utils/axios";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";

import cls from "./ExploreStreetSingle.module.scss"; // ✅ use same SCSS styles as ExploreCitySingle (copy file)

const languageLabel = (code) => {
  const map = {
    en: "English",
    si: "සිංහල (Sinhala)",
    ta: "தமிழ் (Tamil)",
    hi: "हिन्दी (Hindi)",
  };
  return map[code] || code?.toUpperCase() || "";
};

const statusLabel = (status) => {
  if (status === "open") return "Open";
  if (status === "closed") return "Closed";
  return status || "";
};

const getImageFromSlot = (slot) => {
  if (!slot) return null;
  if (typeof slot === "string") return slot;
  if (typeof slot === "object") return slot.url || slot.rawUrl || null;
  return null;
};

const ExploreStreetSingle = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { streetId } = useParams();

  const [street, setStreet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [languageFilter, setLanguageFilter] = useState("all");

  // Same token pattern as ExploreCitySingle
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  // Tabs active state (same as ExploreCitySingle)
  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  useEffect(() => {
    let cancelled = false;

    const loadStreet = async () => {
      if (!streetId) return;

      try {
        setLoading(true);
        setErr("");
        const { data } = await api.get(
          `/v1/lounge/streets/explore/${streetId}`,
        );
        if (!cancelled) setStreet(data || null);
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.response?.data?.message ||
            e?.message ||
            t("exploreStreetSingle.error.generic");
          setErr(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStreet();
    return () => {
      cancelled = true;
    };
  }, [streetId, t]);

  const avatarUrl = useMemo(() => {
    if (!street) return null;

    const thumb = getImageFromSlot(street.avatarThumb);
    if (thumb) return thumb;

    const avatar = getImageFromSlot(street.avatar);
    if (avatar) return avatar;

    return CityImage;
  }, [street]);

  const coverUrl = useMemo(() => {
    if (!street) return null;
    const cover = getImageFromSlot(street.coverImage);
    return cover || CityImage;
  }, [street]);

  // Video id detection (same idea as ExploreCitySingle)
  const videoId = useMemo(() => {
    if (!street) return "";

    if (street.streetVideoId && typeof street.streetVideoId === "string") {
      return street.streetVideoId;
    }
    if (
      street.streetVideoId &&
      typeof street.streetVideoId === "object" &&
      street.streetVideoId._id
    ) {
      return street.streetVideoId._id;
    }
    if (street.streetVideo && street.streetVideo.videoId) {
      return street.streetVideo.videoId;
    }
    if (street.streetVideo && street.streetVideo._id) {
      return street.streetVideo._id;
    }
    return "";
  }, [street]);

  const hasVideo = Boolean(videoId);

  const posterUrl = useMemo(() => {
    if (!street) return null;
    return (
      getImageFromSlot(street.avatar) ||
      getImageFromSlot(street.avatarThumb) ||
      CityImage
    );
  }, [street]);

  const openClubs = useMemo(() => {
    if (!street?.clubs) return [];
    return street.clubs.filter((c) => c.status === "open");
  }, [street]);

  const closedClubs = useMemo(() => {
    if (!street?.clubs) return [];
    return street.clubs.filter((c) => c.status === "closed");
  }, [street]);

  const availableLanguages = useMemo(() => {
    const codes = new Set(
      openClubs
        .map((c) => c.clubLanguage)
        .filter((code) => typeof code === "string" && code.length > 0),
    );
    return Array.from(codes);
  }, [openClubs]);

  const filteredOpenClubs = useMemo(() => {
    if (languageFilter === "all") return openClubs;
    return openClubs.filter((c) => c.clubLanguage === languageFilter);
  }, [openClubs, languageFilter]);

  const getClubImage = (club) => {
    const src = getImageFromSlot(club.avatarThumb);
    return src || CityImage;
  };

  const renderClubCard = (club) => {
    const imageSrc = getClubImage(club);
    const bodyIntro = club.clubIntro || club.clubVibe || "";
    const language = languageLabel(club.clubLanguage);
    const status = statusLabel(club.status);

    const metaText = t("exploreStreetSingle.clubs.meta", {
      language,
      status,
    });

    const isClosed = club.status !== "open";

    return (
      <Motion.div
        key={club.clubId}
        whileHover={{ y: -4, scale: 1.02 }}
        transition={{ duration: 0.16 }}
      >
        <div className={cls.clubCard}>
          <div className={cls.clubImageWrap}>
            <img src={imageSrc} alt={club.clubName} className={cls.clubImage} />
          </div>

          <div className={cls.clubBody}>
            <h3 className={cls.clubName}>{club.clubName}</h3>
            <p className={cls.clubMeta}>{metaText}</p>
            {bodyIntro && <p className={cls.clubIntro}>{bodyIntro}</p>}
          </div>

          <div className={cls.clubFooter}>
            {isClosed ? (
              <button
                type="button"
                className={`${cls.clubVisitBtn} ${cls.clubVisitBtnDisabled}`}
                disabled
              >
                {t("exploreStreetSingle.clubs.closedBtn")}
              </button>
            ) : (
              <Link
                to={`/lounge/clubs/single/${club.clubId}`}
                className={cls.clubVisitBtn}
              >
                {t("exploreStreetSingle.clubs.viewBtn")}
              </Link>
            )}
          </div>
        </div>
      </Motion.div>
    );
  };

  // ✅ Loader gate AFTER hooks (safe)
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  if (!street || err) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          <p className={cls.errorText}>
            {err || t("exploreStreetSingle.error.notFound")}
          </p>
        </div>
      </div>
    );
  }

  const streetLanguage = languageLabel(street.streetLanguage);
  const streetStatus = statusLabel(street.streetStatus);

  const heroMeta = t("exploreStreetSingle.hero.meta", {
    language: streetLanguage,
    status: streetStatus,
  });

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row – same structure as ExploreCitySingle */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        {/* Main layout – single centered column */}
        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA – same 3-button row */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h7v-3.5c0-1.19.41-2.27 1.09-3.21C8.61 13.11 8.31 13 8 13zm8 0c-.32 0-.62.11-.91.17.68.94 1.09 2.02 1.09 3.33V20h7v-3.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.19 15.19C14.74 18.64 13.02 19 12 19c-2.76 0-5-2.24-5-5 0-1.02.36-2.74 1.81-4.19C10.26 8.36 11.98 8 13 8c2.76 0 5 2.24 5 5 0 1.02-.36 2.74-1.81 4.19z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C8.01 14 6 11.99 6 9.5S8.01 5 10.5 5 15 7.01 15 9.5 12.99 14 10.5 14z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* HERO: street cover + avatar + basic info */}
            <section className={cls.searchHero}>
              <div className={cls.searchHeroStage}>
                <Motion.div
                  className={cls.heroFrame}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <div
                    className={cls.heroCover}
                    style={
                      coverUrl
                        ? { backgroundImage: `url(${coverUrl})` }
                        : undefined
                    }
                  >
                    <div className={cls.heroOverlay} />
                    <div className={cls.heroContentRow}>
                      <div className={cls.heroAvatarWrap}>
                        <div className={cls.heroAvatarGlow}>
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={street.streetName}
                              className={cls.heroAvatarImage}
                            />
                          ) : (
                            <div className={cls.heroAvatarFallback}>
                              {street.streetName?.[0] || "S"}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={cls.heroText}>
                        <span className={cls.heroLabel}>
                          {t("exploreStreetSingle.hero.label")}
                        </span>
                        <h1 className={cls.heroTitle}>{street.streetName}</h1>
                        {street.streetIntro && (
                          <p className={cls.heroIntro}>{street.streetIntro}</p>
                        )}
                        <p className={cls.heroMeta}>{heroMeta}</p>
                      </div>
                    </div>
                  </div>
                </Motion.div>

                {/* Front glass card */}
                <Motion.div
                  className={cls.heroCard}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.1 }}
                >
                  <span className={cls.searchPromptLabel}>
                    {t("exploreStreetSingle.hero.cardLabel")}
                  </span>
                  <h2 className={cls.searchPromptTitle}>
                    {t("exploreStreetSingle.hero.cardTitle", {
                      streetName: street.streetName,
                    })}
                  </h2>
                  <p className={cls.searchPromptText}>
                    {street.streetDescription ||
                      t("exploreStreetSingle.hero.cardTextFallback")}
                  </p>
                </Motion.div>
              </div>
            </section>

            {/* VIDEO */}
            <section className={cls.videoSection}>
              <div className={cls.card}>
                <div className={cls.cardHeaderRow}>
                  <h2 className={cls.cardTitle}>
                    {t("exploreStreetSingle.video.cardTitle")}
                  </h2>
                  <p className={cls.cardMeta}>
                    {t("exploreStreetSingle.video.cardSubtitle")}
                  </p>
                </div>

                <div className={cls.videoPlayerWrap}>
                  {hasVideo ? (
                    <VideoPlayer
                      token={token}
                      videoId={videoId}
                      poster={posterUrl}
                      mode="proxy"
                      height={220}
                    />
                  ) : (
                    <div className={cls.noVideo}>
                      {t("exploreStreetSingle.video.noVideo")}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* CLUBS GRID (open + closed) */}
            <section className={cls.clubsGridSection}>
              <div className={cls.clubsGridHeader}>
                <h2 className={cls.cityListTitle}>
                  {t("exploreStreetSingle.clubs.openTitle")}
                </h2>
                <p className={cls.cityListMeta}>
                  {t("exploreStreetSingle.clubs.openMeta")}
                </p>
              </div>

              <div className={cls.filtersRow}>
                <span className={cls.filterLabel}>
                  {t("exploreStreetSingle.filters.languageLabel")}
                </span>
                <select
                  className={cls.filterSelect}
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                >
                  <option value="all">
                    {t("exploreStreetSingle.filters.allLanguages")}
                  </option>
                  {availableLanguages.map((code) => (
                    <option key={code} value={code}>
                      {languageLabel(code)}
                    </option>
                  ))}
                </select>
              </div>

              {filteredOpenClubs.length === 0 ? (
                <p className={cls.errorText}>
                  {t("exploreStreetSingle.clubs.noOpen")}
                </p>
              ) : (
                <div className={cls.clubsGrid}>
                  {filteredOpenClubs.map((c) => renderClubCard(c))}
                </div>
              )}

              {closedClubs.length > 0 && (
                <div className={cls.closedSection}>
                  <div className={cls.clubsGridHeader}>
                    <h2 className={cls.cityListTitle}>
                      {t("exploreStreetSingle.clubs.closedTitle")}
                    </h2>
                    <p className={cls.cityListMeta}>
                      {t("exploreStreetSingle.clubs.closedMeta")}
                    </p>
                  </div>

                  <div className={cls.clubsGrid}>
                    {closedClubs.map((c) => renderClubCard(c))}
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreStreetSingle;
